import { forwardRef } from "react";
import { motion } from "framer-motion";
import { Trash2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { formatTime, stripMarkupAndMarkdown } from "@/lib/textUtils";
import type { DataItem } from "@/types";

interface HistoryItemRowProps {
  itemId: string;
  data: DataItem;
  createdAt: number;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}

export const HistoryItemRow = forwardRef<HTMLDivElement, HistoryItemRowProps>(({
  itemId,
  data,
  createdAt,
  isSelected,
  onClick,
  onDelete,
  ...props
}, ref) => {
  const { t } = useI18n();

  return (
    <motion.div
      ref={ref}
      {...props}
      layout="position"
      initial={{ x: 10, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -10, opacity: 0 }}
      transition={{ duration: 0.15 }}
      onClick={onClick}
      data-selectable-id={itemId}
      data-selectable-data-id={data.id}
      data-selectable-area="history"
      data-testid="history-item"
      className={`glass-bar rounded-lg border-2 px-3 py-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 group cursor-pointer transition-all min-w-0 ${
        isSelected
          ? "border-primary bg-primary/10 shadow-[0_0_8px_hsl(var(--primary)/0.3)]"
          : "border-border/40 hover:border-primary/40"
      }`}
    >
      <div className="min-w-0 flex items-center">
        {data.type === "image" ? (
          <img
            src={data.content}
            alt={data.label}
            className="w-8 h-8 rounded object-cover shrink-0"
          />
        ) : (
          <span className="block min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs text-foreground/80">
            {stripMarkupAndMarkdown(data.content)}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-[10px] text-muted-foreground/60">
          {formatTime(createdAt)}
        </span>
        <button
          onClick={onDelete}
          aria-label={t("menu.delete")}
          data-testid="history-item-delete"
          className="p-1 rounded-md text-muted-foreground/70 hover:text-destructive hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-colors"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </motion.div>
  );
});

HistoryItemRow.displayName = "HistoryItemRow";
